import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import whatsappService from '@/services/api/whatsapp.service';
import webSocketService from '@/services/websocket.service';
import { createVisibleRefresh } from '@/utils/visibleRefresh';

// Unread-WhatsApp badge feed for the green icon next to every phone number.
//
// Shape: ONE global map (`{phones: {digits: {unread, conversations}}, contacts:
// {...}}`) covering every phone that currently has unread inbound, rather than a
// per-row or per-page request. There are ~15 such phones at any time, so the map
// is smaller than a single list page, and a 50-row list reads it with one
// request — every icon on the page shares this query key, so react-query
// deduplicates them into a single fetch.
//
// Freshness: WebSocket-primary. The backend broadcasts `whatsapp_update` on
// every message and receipt, and useWhatsAppUnreadInvalidation (mounted once in
// App) invalidates this key when one lands, so a badge appears a second or two
// after the customer's message. The interval below is only the safety net for a
// dropped socket. The shared feed is disabled while the tab is hidden.
export const WHATSAPP_UNREAD_QUERY_KEY = ['whatsapp', 'unreadByPhone'];

const EMPTY = { phones: {}, contacts: {} };
const EMPTY_BY_OWNER = {};

// Mirrors `_normalize_phone_digits` on the backend: digits only, Romanian local
// 07xx… widened to 407xx… so both forms hit the same map key.
export const whatsAppPhoneDigits = (raw) => {
  if (!raw) return '';
  let digits = String(raw).replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length === 10) digits = `4${digits}`;
  return digits.length >= 5 ? digits : '';
};

export const useWhatsAppUnreadMap = () => {
  const { data } = useQuery({
    queryKey: WHATSAPP_UNREAD_QUERY_KEY,
    queryFn: () => whatsappService.getUnreadByPhone(),
    // Fetching belongs to the single useWhatsAppBadgeFeeds owner in AppRoutes.
    enabled: false,
  });
  return data || EMPTY;
};

// Unread count for one phone number, falling back to the CRM contact hash —
// that covers chats WhatsApp migrated to an opaque @lid address, which carry no
// phone number to match on.
export const useWhatsAppUnreadCount = (phone, contactHashId) => {
  const map = useWhatsAppUnreadMap();
  const digits = whatsAppPhoneDigits(phone);
  const byPhone = digits ? map.phones?.[digits] : null;
  if (byPhone) return Number(byPhone.unread || 0);
  if (contactHashId) {
    const byContact = map.contacts?.[String(contactHashId).toUpperCase()];
    if (byContact) return Number(byContact.unread || 0);
  }
  return 0;
};

// Unread counts for one phone number, split by WHICH of our accounts they sit
// on: `{ownerUserId: count}`. The same customer chat exists once per sender
// account, so the drawer's account picker uses this to point at the channel that
// actually has something waiting.
export const useWhatsAppUnreadByOwner = (phone, contactHashId) => {
  const map = useWhatsAppUnreadMap();
  const digits = whatsAppPhoneDigits(phone);
  const entry =
    (digits ? map.phones?.[digits] : null)
    || (contactHashId ? map.contacts?.[String(contactHashId).toUpperCase()] : null);
  return entry?.by_owner || EMPTY_BY_OWNER;
};

// Mounted ONCE app-wide. Same shape as useRecordChangeListInvalidation: throttle
// the burst, skip while the tab is hidden, catch up on focus and on reconnect.
export const useWhatsAppUnreadInvalidation = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    let unreadPending = false;
    let agentPending = false;
    const refresh = createVisibleRefresh(async () => {
      const queries = [];
      if (unreadPending) queries.push(WHATSAPP_UNREAD_QUERY_KEY);
      if (agentPending) queries.push(['whatsapp', 'agentRunning']);
      unreadPending = false;
      agentPending = false;
      await Promise.all(queries.map((queryKey) => queryClient.invalidateQueries(
        { queryKey }, { cancelRefetch: false },
      )));
    }, 1500);
    const all = () => {
      unreadPending = true;
      agentPending = true;
      refresh.schedule();
    };
    const unsubscribe = webSocketService.addWhatsAppUpdateListener((update) => {
      if (update?.action === 'message') {
        all();
      } else if (update?.action === 'agent_plan') {
        agentPending = true;
        refresh.schedule();
      } else if (update?.action === 'receipt') {
        // Delivery acknowledgements change outgoing ticks, never unread badges.
        // Unknown receipt types still refresh for older gateway versions.
        const type = String(update.receipt_type || '').toLowerCase();
        if (!['delivery', 'delivered', 'retry', 'server-error'].includes(type)) {
          unreadPending = true;
          refresh.schedule();
        }
      }
    });
    const unsubscribeReconnect = webSocketService.addReconnectListener(all);
    return () => {
      refresh.dispose();
      unsubscribe();
      unsubscribeReconnect();
    };
  }, [queryClient]);
};

export default useWhatsAppUnreadMap;
